import React, {useState} from 'react';

import {assert} from 'Asserts';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as BlobActions from 'schemas/tags/BlobActions';
import BlobInputModal from 'schemas/tags/BlobInputModal';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import DropTarget from 'util/drag-and-drop/DropTarget';
import {ElementDragEvent} from 'util/Types';

import {AutomationErrorFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationErrorFileGraphQL';
import * as AutomationFileDAO from './AutomationFileDAO';

interface UploadAutomationErrorFileButtonProps {
  runId: string | null;
  automationErrorFile: AutomationErrorFileGraphQL;
  disabled: boolean;
  updateAutomationErrorFile: (automationErrorFile: AutomationErrorFileGraphQL) => void;
}

export const UploadAutomationErrorFileButton: React.FunctionComponent<
  UploadAutomationErrorFileButtonProps
> = (props: UploadAutomationErrorFileButtonProps): JSX.Element => {
  const [isUploading, setIsUploading] = useState(false);
  const onUploadFiles = (files: Array<File>): JQueryPromise<void> => {
    setIsUploading(true);
    return BlobActions.uploadFilesAsBlobs(files, {
      shouldGeneratePreviewFile: false,
      onBlobsCreated: async (newBlobs) => {
        if (newBlobs.length === 0) {
          return;
        }
        assert(newBlobs.length === 1, 'Tried to upload multiple blobs to automation error file');
        const blobId = newBlobs[0].id;
        const response = await AutomationFileDAO.updateAutomationErrorFileBlob(
          props.runId!,
          props.automationErrorFile.automationOutputFileConfig.name,
          blobId
        );
        props.updateAutomationErrorFile(response.automationErrorFile);
      },
    }).done(() => {
      setIsUploading(false);
    });
  };
  return (
    <DropTarget
      shouldCaptureDrop={true}
      validateDrop={(): true | null => (props.disabled ? true : null)}
      mimeTypes={['Files']}
      dropEffect="link"
      targetDesc="additional files"
      onDrop={(e: ElementDragEvent): void => {
        const files = e.dataTransfer.files;
        if (files.length) {
          onUploadFiles([files[0]]);
        }
      }}
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        fullWidth={true}
        text={isUploading ? 'Uploading...' : 'Upload file'}
        icon={isUploading ? <BenchlingIcon icon="refresh" className="glyphicon-spin" /> : 'upload'}
        disabled={props.disabled}
        textClassName="button-text--nowrap"
        onClick={() => {
          legacyRunModal(BlobInputModal, {
            allowMultiple: false,
            onUploadFiles,
          });
        }}
      />
    </DropTarget>
  );
};

interface RunAutomationErrorFileViewerProps {
  runId: string | null;
  automationErrorFile: AutomationErrorFileGraphQL | null;
  locked: boolean;
  updateAutomationErrorFile: (automationErrorFile: AutomationErrorFileGraphQL) => void;
}

/**
 * This component represents an AutomationErrorFile in a NoteLinkedAssayData.
 *
 * What is rendered is a function of the status of the AutomationErrorFile.
 */
const RunAutomationErrorFileViewer: React.FunctionComponent<RunAutomationErrorFileViewerProps> = (
  props: RunAutomationErrorFileViewerProps
): JSX.Element | null => {
  if (props.automationErrorFile == null) {
    return null;
  }
  return (
    <Row className="u-mini-spacing-top" bufferRowReset={true}>
      <Col md={3}>
        <span className="fieldLabel-sublabel">Additional file</span>
      </Col>
      <Col md={9}>
        {props.automationErrorFile.status === AutomationFileStatusGraphQL.succeeded ? (
          <Chip
            className="chip-container"
            icon="paperclip"
            size={ChipSize.MINI}
            text={props.automationErrorFile.blob?.name}
            href={props.automationErrorFile.blob?.url}
            onRemove={
              !props.locked
                ? async () => {
                    const response = await AutomationFileDAO.updateAutomationErrorFileBlob(
                      props.runId!,
                      props.automationErrorFile!.automationOutputFileConfig.name,
                      null
                    );
                    props.updateAutomationErrorFile(response.automationErrorFile);
                  }
                : null
            }
          />
        ) : (
          <UploadAutomationErrorFileButton
            runId={props.runId}
            automationErrorFile={props.automationErrorFile}
            disabled={props.locked}
            updateAutomationErrorFile={props.updateAutomationErrorFile}
          />
        )}
      </Col>
    </Row>
  );
};

export default RunAutomationErrorFileViewer;
